import { ComponentsOverrides, ComponentsProps, Popper, Theme } from "@mui/material";
import { PopperProps } from "@mui/material/Popper";
import React from "react";

import borders from "@/themes/base/borders";
import boxShadows from "@/themes/base/boxShadows";
import colors from "@/themes/base/colors";
import typography from "@/themes/base/typography";
import pxToRem from "@/themes/functions/pxToRem";

const { lg } = boxShadows;
const { size } = typography;
const { text, white, transparent, light, dark, gradients } = colors;
const { borderRadius } = borders;

/**
 * Com #root { zoom }, clientWidth é layout e o popper vive no body (sem zoom).
 * Usar a largura visual do âncora para o dropdown acompanhar o input.
 */
function AutocompleteZoomAwarePopper({ anchorEl, style, ...other }: PopperProps) {
  const visualWidth =
    anchorEl && "getBoundingClientRect" in anchorEl
      ? (anchorEl as Element).getBoundingClientRect().width
      : undefined;

  return React.createElement(Popper, {
    ...other,
    anchorEl,
    style: {
      ...style,
      width: visualWidth ?? style?.width,
    },
  });
}

interface IAutoComplete {
  defaultProps: ComponentsProps["MuiAutocomplete"];
  styleOverrides: ComponentsOverrides<Theme>["MuiAutocomplete"];
}

const autocomplete: IAutoComplete = {
  defaultProps: {
    slots: {
      popper: AutocompleteZoomAwarePopper,
    },
  },

  styleOverrides: {
    popper: {
      boxShadow: lg,
      padding: `calc(${pxToRem(8)} * var(--app-zoom))`,
      fontSize: `calc(${size.sm} * var(--app-zoom))`,
      color: text!.main,
      textAlign: "left",
      backgroundColor: `${white.main} !important`,
      borderRadius: borderRadius.md,
    },

    paper: {
      boxShadow: "none",
      backgroundColor: transparent.main,
    },

    option: {
      padding: `calc(${pxToRem(4.8)} * var(--app-zoom)) calc(${pxToRem(16)} * var(--app-zoom))`,
      borderRadius: borderRadius.md,
      fontSize: `calc(${size.sm} * var(--app-zoom))`,
      color: text!.main,
      transition: "background-color 300ms ease, color 300ms ease",

      "&:hover, &:focus, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
        backgroundColor: light.main,
        color: dark.main,
      },

      // eslint-disable-next-line quotes
      '&[aria-selected="true"]': {
        backgroundColor: `${light.main} !important`,
        color: `${dark.main} !important`,
      },
    },

    noOptions: {
      fontSize: `calc(${size.sm} * var(--app-zoom))`,
      color: text!.main,
    },

    groupLabel: {
      color: dark.main,
    },

    loading: {
      fontSize: `calc(${size.sm} * var(--app-zoom))`,
      color: text!.main,
    },

    tag: {
      display: "flex",
      alignItems: "center",
      height: "auto",
      padding: pxToRem(4),
      backgroundColor: gradients.dark.state,
      color: white.main,

      "& .MuiChip-label": {
        lineHeight: 1.2,
        padding: `0 ${pxToRem(10)} 0 ${pxToRem(4)}`,
      },

      "& .MuiSvgIcon-root, & .MuiSvgIcon-root:hover, & .MuiSvgIcon-root:focus": {
        color: white.main,
        marginRight: 0,
      },
    },
  },
};

export default autocomplete;
